export enum SubscriptionSchemeId {
  trial = 'trial',
  readOnly = 'read_only',
  standard = 'standard',
  legacy = 'legacy',
  free = 'free',
  readOnlyFree = 'read_only_free',
  lifeTime = 'lifetime_album',
}

export enum SubscriptionStatus {
  subscriptionActive = 'subscription_active',

  firstPaymentFailed = 'first_payment_failed',

  paymentPending = 'payment_pending',
  paymentFailed = 'payment_failed',

  subscriptionCanceled = 'subscription_canceled',
  subscriptionOverdue = 'subscription_overdue',
  subscriptionEnded = 'subscription_ended',
  // A storage-plan vault whose Checkout isn't paid: a locked draft.
  awaitingCheckout = 'awaiting_checkout',
}

export enum Breakpoints {
  xl = 1400,
  lg = 1200,
  md = 992,
  sm = 768,
  xs = 576,
}

export enum TimeUnit {
  milliseconds = 'milliseconds ',
  seconds = 'seconds',
  minutes = 'minutes',
  hours = 'hours',
}

export enum BasicOrderByValues {
  alphabeticallyAZ = 'Vault name, A-Z',
  alphabeticallyZA = 'Vault name, Z-A',
  lastUpdate = 'Last Update',
  oldestUpdate = 'Oldest Update',
}

export enum Countries {
  UnitedStates = 'United States',
  Other = 'Other',
}

export enum SubModificationType {
  upgrade = 'upgrade',
  downgrade = 'downgrade',
}

export enum InvitationStatusId {
  accepted = 'accepted',
  pending = 'pending',
  rejected = 'rejected',
  removed = 'removed',
  expired = 'expired',
}

// Album invite-by-email roles/statuses (distinct from the vault-collaborator
// InvitationStatusId, which uses "rejected" rather than "declined").
// What someone an album is shared with may do: view it; also add memories
// (and change their own); or also change and delete anything in it.
export enum AlbumMemberRole {
  viewer = 'viewer',
  contributor = 'contributor',
  editor = 'editor',
}

export enum AlbumMemberStatus {
  invited = 'invited',
  active = 'active',
  declined = 'declined',
}

export enum AllContributorsFiltersIds {
  accepted = 'accepted',
  pending = 'pending',
  rejected = 'rejected',
}

export enum AllVaultsFiltersIds {
  active = 'active',
  restricted = 'restricted',
  locked = 'locked',
}

export enum NavSections {
  vaults = 'Vaults',
  collaborators = 'Collaborators',
  templates = 'My Templates',
  account = 'Account',
  store = 'Store',
  logout = 'Logout',
}

// What a collaborator may do with a vault's content. Separate from whether
// they can see the whole vault, which is the `canViewVault` flag.
export enum VaultCollaboratorRole {
  viewer = 'viewer',
  contributor = 'contributor',
  editor = 'editor',
}

export enum CreateVaultSteps {
  information = 1,
  plan = 2,
  confirmation = 3,
}

export const CREATE_VAULT_STEP_COUNT = 3;

export enum VaultTabs {
  albums = 'Albums',
  clips = 'Clips',
  addPrompts = 'Add prompts',
  recordAnswers = 'Record answers',
  videoCall = 'Video call',
  playlists = 'Playlists',
  upgradeVault = 'Upgrade vault',
  settings = 'Settings',
  upload = 'Upload',
  selfRecording = 'Self recording',
}

export enum VaultNavigationTabs {
  dashboard = 'Dashboard',
  albums = 'Albums',
  memories = 'Memories',
  timeline = 'Timeline',
  planner = 'Planner',
}

export enum AlbumsFilters {
  all = 'All',
  videos = 'Videos',
  images = 'Images',
  audio = 'Audio',
  notes = 'Notes',
  documents = 'Documents',
}

export enum VideoCallMenuQuestionsTabs {
  pending = 'pending',
  recorded = 'recorded',
}

export enum FileExtension {
  // Images
  Jpg = '.jpg',
  Jpeg = '.jpeg',
  Png = '.png',
  Mp4 = '.mp4',
  Webm = '.webm',
  Mp3 = '.mp3',
  Wav = '.wav',
  M4a = '.m4a',
  Aac = '.aac',
  Ogg = '.ogg',
  Pdf = '.pdf',
  Doc = '.doc',
  Docx = '.docx',
  Txt = '.txt',
  Rtf = '.rtf',

  Markdown = '.md',
  Text = '.txt',
  RichText = '.rtf',
}

export enum NotificationType {
  Error = 'Error',
  Success = 'Success',
  Info = 'Info',
}

export enum PaymentType {
  CreateNewVault = 'CreateNewVault',
  ModifyVaultSubscription = 'ModifyVaultSubscription',
}

export enum QuestionsManagmentViewType {
  editVault = 'editVault',
  myTemplates = 'myTemplates',
}

export enum RecordingStatus {
  noVideo = 'no_video',
  recorded = 'recorded',
  processing = 'processing',
}

export enum VideoStatus {
  new = 'new',
  inProgress = 'inProgress',
  ready = 'ready',
  failed = 'failed',
}

export enum ClipActions {
  select = 'select',
  delete = 'delete',
  togglePrivate = 'togglePrivate',
  editClip = 'editClip',
  toggleAudioOnly = 'toggleAudioOnly',
}
export enum VaultOwnershipFiltersIds {
  owner = 'owner',
  contributor = 'contributor',
}

export enum VaultStatusFiltersIds {
  active = 'active',
  restricted = 'restricted',
  locked = 'locked',
}

export enum VaultTypeFiltersIds {
  legacy = 'legacy',
  lifetime = 'lifetime_album',
  standard = 'standard',
  readOnly = 'read_only',
}

export enum QrCodesFiltersIds {
  withQrCodes = 'with_qr_codes',
  withoutQrCodes = 'without_qr_codes',
}

export enum CollaboratorTypeFilterIds {
  contributors = 'contributors',
  guests = 'guests',
}

export enum VaultInviteOriginFilterIds {
  myself = 'myself',
  otherContributor = 'other_contributor',
}

export enum CollaboratorStatusFiltersIds {
  active = 'active',
  pending = 'pending',
  removed = 'removed',
  expired = 'expired',
}

export enum SelfRecordingStatus {
  recorded = 'recorded',
  processing = 'processing',
  error = 'error',
}

export enum RecordingState {
  READY_TO_RECORD = 'ready_to_record',
  RECORDING = 'recording',
  PAUSED = 'paused',
}

export enum PaymentStatus {
  pending = 'pending',
  failed = 'failed',
  paid = 'paid',
}

export enum UserTypeId {
  Standard = 'standard',
  Temporary = 'temporary',
}

export enum NavbarTabs {
  vaults = 'vaults',
  collaborators = 'collaborators',
  store = 'store',
}

export enum AlbumsSortBy {
  recentActivity = 'recent_activity',
  oldestActivity = 'oldest_activity',
  // By when it was added (created), rather than the date set on it.
  recentlyAdded = 'recently_added',
  earliestAdded = 'earliest_added',
  aToZ = 'a-z',
  zToA = 'z-a',
  // The vault's own order for its memories (and the planner's for albums);
  // offered only once the vault has one.
  custom = 'custom',
}

export enum AlbumAddContentTypes {
  video = 'video',
  audio = 'audio',
  write = 'write',
  upload = 'document',
}
export enum AlbumContentType {
  video = 'video',
  audio = 'audio',
  write = 'journal_entry',
  image = 'image',
  document = 'document',
}

export enum TopicTab {
  SUGGESTED = 'Suggested',
  CUSTOM = 'Custom',
  MY_TEMPLATES = 'My Templates',
}
